import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { api } from "@shared/routes";
import { useAuth } from "@/hooks/use-auth";

function showBrowserNotification(title: string, body: string) {
  if (!("Notification" in window)) return;
  if (Notification.permission === "granted") {
    new Notification(title, { body, icon: "/favicon.ico" });
  } else if (Notification.permission === "default") {
    Notification.requestPermission().then((permission) => {
      if (permission === "granted") {
        new Notification(title, { body, icon: "/favicon.ico" });
      }
    });
  }
}

/**
 * Subscribes to Supabase Realtime postgres changes for the ReMonta tables published by
 * supabase/migrations (audit_logs, empresas, user_profiles, veiculos, remonta_laudos,
 * invite_codes, user_invites, notificacoes) and invalidates the
 * corresponding React Query caches so the UI stays in sync with external
 * changes (e.g. from the mobile app).
 *
 * Must be mounted inside AuthProvider and QueryClientProvider.
 */
export function useRealtimeSync() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  useEffect(() => {
    // Only subscribe while authenticated
    if (!user) return;

    const channel = supabase
      .channel("remonta-realtime")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "audit_logs" },
        () => {
          queryClient.invalidateQueries({
            queryKey: [api.auditoria.list.path],
          });
        },
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "empresas" },
        () => {
          queryClient.invalidateQueries({ queryKey: [api.empresas.list.path] });
          queryClient.invalidateQueries({ queryKey: [api.empresas.get.path] });
          queryClient.invalidateQueries({ queryKey: [api.empresas.parceiros.path] });
          queryClient.invalidateQueries({ queryKey: [api.veiculos.parceiros.path] });
        },
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "user_profiles" },
        () => {
          queryClient.invalidateQueries({ queryKey: [api.usuarios.list.path] });
        },
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "veiculos" },
        () => {
          queryClient.invalidateQueries({ queryKey: [api.veiculos.list.path] });
        },
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "remonta_laudos" },
        () => {
          queryClient.invalidateQueries({ queryKey: [api.remontaLaudos.list.path] });
          queryClient.invalidateQueries({ queryKey: [api.remontaLaudos.get.path] });
        },
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "invite_codes" },
        () => {
          queryClient.invalidateQueries({ queryKey: [api.inviteCodes.list.path] });
        },
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "user_invites" },
        () => {
          queryClient.invalidateQueries({ queryKey: [api.userInvites.list.path] });
        },
      )
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "notificacoes" },
        (payload) => {
          queryClient.invalidateQueries({ queryKey: [api.notificacoes.list.path] });
          const row = payload.new as Record<string, unknown>;
          if (row.usuario_id === user?.id) {
            showBrowserNotification(
              String(row.titulo ?? "Nova notificação"),
              String(row.mensagem ?? ""),
            );
          }
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [queryClient, user]);
}
